import {useEffect, useMemo, useState} from 'react';

import PropTypes from 'prop-types';

import {Box} from '@salesforce/retail-react-app/app/components/shared/ui';

import {Component} from '@amblique/king-living/app/components/contentful/component';
import {
  HERO_BANNER_DESKTOP_MEDIA_QUERY,
  HeroBannerBackgroundVideo,
  readIsDesktopHeroViewport,
  resolveActiveHeroBackgroundVideo,
  resolveHeroBackgroundLayers,
} from '@amblique/king-living/app/utils/hero-banner-video';

/** Image, or a single video for the current viewport (lg+ = desktop). */
export const HeroBannerBackgroundMedia = ({
  backgroundMedia,
  backgroundVideoMobile,
  videoOptions,
  mobileVideoOptions,
  height,
  sx,
  ...mediaProps
}) => {
  const {backgroundImage, desktopVideo, mobileVideo} = useMemo(
    () => resolveHeroBackgroundLayers({backgroundMedia, backgroundVideoMobile}),
    [backgroundMedia, backgroundVideoMobile]
  );

  const needsViewport = !backgroundImage && Boolean(desktopVideo || mobileVideo);
  const [isDesktop, setIsDesktop] = useState(() => {
    return needsViewport ? readIsDesktopHeroViewport() : undefined;
  });

  useEffect(() => {
    if (!needsViewport || typeof window?.matchMedia !== 'function') return undefined;

    const mq = window.matchMedia(HERO_BANNER_DESKTOP_MEDIA_QUERY);
    const sync = () => setIsDesktop(mq.matches);

    sync();
    mq.addEventListener('change', sync);

    return () => mq.removeEventListener('change', sync);
  }, [needsViewport]);

  if (backgroundImage) {
    return (
      <Component
        component={backgroundImage}
        fetchpriority="high"
        loading="eager"
        height={height}
        sx={sx}
        {...mediaProps}
      />
    );
  }

  // Wait for viewport so we do not load the wrong video.
  if (isDesktop === undefined) return null;

  const activeVideo = resolveActiveHeroBackgroundVideo({desktopVideo, mobileVideo}, isDesktop);

  if (!activeVideo) return null;

  const activeVideoOptions = isDesktop ? videoOptions : mobileVideoOptions ?? videoOptions;
  const isMobileNaturalVideo = !isDesktop && activeVideoOptions?.size === 'contain';

  const layerClass = [
    'hero-banner__video-layer',
    isDesktop ? 'hero-banner__video-layer--desktop' : 'hero-banner__video-layer--mobile',
    isMobileNaturalVideo && 'hero-banner__video-layer--natural-ratio',
  ]
    .filter(Boolean)
    .join(' ');
  const resolvedHeight = isMobileNaturalVideo ? undefined : height ?? 'auto';
  const resolvedSx =
    isMobileNaturalVideo && sx ?
      {
        ...sx,
        'img, video': {
          height: 'auto',
          objectFit: 'contain',
          width: '100%',
        },
      } :
      sx;

  return (
    <Box className={layerClass} height={resolvedHeight} width="100%">
      <HeroBannerBackgroundVideo
        component={activeVideo}
        videoOptions={activeVideoOptions}
        {...mediaProps}
        height={resolvedHeight}
        sx={resolvedSx}
      />
    </Box>
  );
};

HeroBannerBackgroundMedia.displayName = 'HeroBannerBackgroundMedia';
HeroBannerBackgroundMedia.propTypes = {
  backgroundMedia: PropTypes.object,
  backgroundVideoMobile: PropTypes.object,
  videoOptions: PropTypes.object,
  mobileVideoOptions: PropTypes.object,
};
